import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Lock, Shield, Smartphone, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  PaymentDetails,
  IDEALPaymentDetails,
  PayPalPaymentDetails,
  validateCardNumber,
  validateExpiryDate,
  validateCVV,
  formatCardNumber,
  getCardBrand,
  savePaymentMethod,
  processPayment,
  IDEAL_BANKS,
  type PaymentMethod,
  type PaymentType
} from '../utils/payments';

interface PaymentFormProps {
  amount: number;
  isRecurring: boolean;
  donorInfo: {
    name: string;
    email: string;
    message?: string;
  };
  onSuccess: (transactionId: string, paymentMethod: PaymentMethod) => void;
  onBack: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  isRecurring,
  donorInfo,
  onSuccess,
  onBack
}) => {
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>('credit_card');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: donorInfo.name || '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
  });

  const [idealDetails, setIdealDetails] = useState<IDEALPaymentDetails>({
    bank: '',
    accountHolder: donorInfo.name || '',
    country: 'NL',
  });

  const [paypalDetails, setPaypalDetails] = useState<PayPalPaymentDetails>({
    email: donorInfo.email || '',
  });

  const [saveCard, setSaveCard] = useState(false);
  const [cardNickname, setCardNickname] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Check if payment methods are available
  const isApplePayAvailable = () => {
    return window.ApplePaySession && window.ApplePaySession.canMakePayments();
  };

  const isGooglePayAvailable = () => {
    return window.google?.payments?.api;
  };

  const paymentMethods = [
    { 
      type: 'credit_card' as PaymentType, 
      name: 'Credit Card', 
      icon: CreditCard,
      available: true,
      description: 'Visa, Mastercard, Amex'
    },
    { 
      type: 'ideal' as PaymentType, 
      name: 'iDEAL', 
      icon: Globe,
      available: true,
      description: 'Dutch bank transfer',
      disabled: isRecurring // iDEAL doesn't support recurring
    },
    { 
      type: 'paypal' as PaymentType, 
      name: 'PayPal', 
      icon: Globe,
      available: true,
      description: 'Pay with PayPal account'
    },
    { 
      type: 'apple_pay' as PaymentType, 
      name: 'Apple Pay', 
      icon: Smartphone,
      available: isApplePayAvailable(),
      description: 'Touch ID or Face ID'
    },
    { 
      type: 'google_pay' as PaymentType, 
      name: 'Google Pay', 
      icon: Smartphone,
      available: isGooglePayAvailable(),
      description: 'Quick and secure'
    },
  ].filter(method => method.available && !method.disabled);

  const updatePaymentDetail = (field: string, value: string) => {
    setPaymentDetails(prev => {
      if (field.startsWith('billingAddress.')) {
        const addressField = field.split('.')[1];
        return {
          ...prev,
          billingAddress: {
            ...prev.billingAddress,
            [addressField]: value,
          },
        };
      }
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const updateIdealDetail = (field: keyof IDEALPaymentDetails, value: string) => {
    setIdealDetails(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const updatePaypalDetail = (field: keyof PayPalPaymentDetails, value: string) => {
    setPaypalDetails(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedPaymentType === 'credit_card') {
      // Card number validation
      if (!paymentDetails.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!validateCardNumber(paymentDetails.cardNumber)) {
        newErrors.cardNumber = 'Invalid card number';
      }

      // Cardholder name validation
      if (!paymentDetails.cardholderName.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }

      // Expiry validation
      if (!paymentDetails.expiryMonth || !paymentDetails.expiryYear) {
        newErrors.expiry = 'Expiry date is required';
      } else if (!validateExpiryDate(paymentDetails.expiryMonth, paymentDetails.expiryYear)) {
        newErrors.expiry = 'Card has expired';
      }

      // CVV validation
      const cardBrand = getCardBrand(paymentDetails.cardNumber);
      if (!paymentDetails.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (!validateCVV(paymentDetails.cvv, cardBrand)) {
        newErrors.cvv = `CVV must be ${cardBrand === 'amex' ? '4' : '3'} digits`;
      }

      // Billing address validation
      if (!paymentDetails.billingAddress.street.trim()) {
        newErrors['billingAddress.street'] = 'Street address is required';
      }
      if (!paymentDetails.billingAddress.city.trim()) {
        newErrors['billingAddress.city'] = 'City is required';
      }
      if (!paymentDetails.billingAddress.state.trim()) {
        newErrors['billingAddress.state'] = 'State is required';
      }
      if (!paymentDetails.billingAddress.zipCode.trim()) {
        newErrors['billingAddress.zipCode'] = 'ZIP code is required';
      }

      // Save card validation
      if (saveCard && !cardNickname.trim()) {
        newErrors.cardNickname = 'Please enter a nickname for your card';
      }
    } else if (selectedPaymentType === 'ideal') {
      if (!idealDetails.bank) {
        newErrors.bank = 'Please select your bank';
      }
      if (!idealDetails.accountHolder.trim()) {
        newErrors.accountHolder = 'Account holder name is required';
      }
    } else if (selectedPaymentType === 'paypal') {
      if (!paypalDetails.email) {
        newErrors.email = 'PayPal email is required';
      } else if (!/\S+@\S+\.\S+/.test(paypalDetails.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setIsProcessing(true);

    try {
      let currentPaymentDetails: any = {};
      
      switch (selectedPaymentType) {
        case 'credit_card':
          currentPaymentDetails = paymentDetails;
          break;
        case 'ideal':
          currentPaymentDetails = idealDetails;
          break;
        case 'paypal':
          currentPaymentDetails = paypalDetails;
          break;
        default:
          currentPaymentDetails = {};
      }

      // Process payment
      const paymentResult = await processPayment(amount, currentPaymentDetails, isRecurring, selectedPaymentType);
      
      // Handle different payment results
      if (!paymentResult.success) {
        // Check if it's a redirect payment (iDEAL, PayPal)
        if (paymentResult.redirectUrl) {
          if (selectedPaymentType === 'ideal' || selectedPaymentType === 'paypal') {
            // Show redirect message and simulate redirect
            toast.success(paymentResult.error || 'Redirecting...');
            
            // In a real app, you would redirect to the URL
            // window.open(paymentResult.redirectUrl, '_blank');
            
            // For demo purposes, simulate returning from redirect after 3 seconds
            setTimeout(() => {
              // Simulate successful return from redirect
              const mockPaymentMethod: PaymentMethod = {
                id: 'redirect_' + Date.now(),
                type: selectedPaymentType,
                nickname: selectedPaymentType === 'ideal' ? 
                  `${IDEAL_BANKS.find(b => b.code === idealDetails.bank)?.name} Account` : 
                  'PayPal Account',
                bankName: selectedPaymentType === 'ideal' ? 
                  IDEAL_BANKS.find(b => b.code === idealDetails.bank)?.name : undefined,
                email: selectedPaymentType === 'paypal' ? paypalDetails.email : undefined,
                isDefault: false,
                createdAt: new Date().toISOString(),
              };
              
              // Simulate successful payment after redirect
              const mockTransactionId = selectedPaymentType + '_' + Math.random().toString(36).substr(2, 9);
              onSuccess(mockTransactionId, mockPaymentMethod);
            }, 3000);
            
            return;
          }
        }
        
        // Handle payment error
        throw new Error(paymentResult.error || 'Payment failed');
      }

      // Handle successful payment (credit cards, Apple Pay, Google Pay)
      let savedPaymentMethod: PaymentMethod | null = null;
      if (selectedPaymentType === 'credit_card' && saveCard) {
        savedPaymentMethod = savePaymentMethod(paymentDetails, cardNickname, makeDefault, selectedPaymentType);
      } else {
        // Create a temporary payment method object for the success callback
        savedPaymentMethod = {
          id: 'temp_' + Date.now(),
          type: selectedPaymentType,
          nickname: selectedPaymentType === 'credit_card' ? 'Temporary Card' :
                   selectedPaymentType === 'apple_pay' ? 'Apple Pay' :
                   'Google Pay',
          lastFour: selectedPaymentType === 'credit_card' ? paymentDetails.cardNumber.slice(-4) : undefined,
          brand: selectedPaymentType === 'credit_card' ? getCardBrand(paymentDetails.cardNumber) : undefined,
          isDefault: false,
          createdAt: new Date().toISOString(),
        };
      }

      // Success for immediate payments
      onSuccess(paymentResult.transactionId!, savedPaymentMethod);
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error instanceof Error ? error.message : 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumberInput = (value: string) => {
    const formatted = formatCardNumber(value);
    updatePaymentDetail('cardNumber', formatted);
  };

  const cardBrand = getCardBrand(paymentDetails.cardNumber);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-purple-600 hover:text-purple-700"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Donation
        </button>
        
        <div className="text-right">
          <div className="text-xl font-bold text-purple-600">
            ${amount.toFixed(2)}
          </div>
          {isRecurring && (
            <div className="text-sm text-gray-500">per month</div>
          )}
        </div>
      </div>

      {/* Payment Method Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Payment Method</h3>
        <div className="grid grid-cols-1 gap-3">
          {paymentMethods.map((method) => {
            const Icon = method.icon;
            return (
              <button
                key={method.type}
                type="button"
                onClick={() => setSelectedPaymentType(method.type)}
                className={`p-4 border rounded-lg text-left transition-all duration-200 ${
                  selectedPaymentType === method.type
                    ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-300 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon size={24} className={
                    selectedPaymentType === method.type ? 'text-purple-600' : 'text-gray-400'
                  } />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{method.name}</div>
                    <div className="text-sm text-gray-500">{method.description}</div>
                  </div>
                  <div className={`w-4 h-4 border-2 rounded-full ${
                    selectedPaymentType === method.type
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedPaymentType === method.type && (
                      <div className="w-full h-full bg-purple-500 rounded-full scale-50"></div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Credit Card Form */}
        {selectedPaymentType === 'credit_card' && (
          <>
            {/* Test Card Notice - Remove in production */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Test Card Numbers</h4>
              <div className="text-blue-700 text-sm space-y-1">
                <p><strong>Success:</strong> 4242 4242 4242 4242</p>
                <p><strong>Declined:</strong> 4000 0000 0000 0002</p>
                <p><strong>Expired:</strong> 4000 0000 0000 0069</p>
                <p><strong>Invalid CVC:</strong> 4000 0000 0000 0127</p>
                <p>Use any future expiry date and any 3-digit CVC.</p>
              </div>
            </div>

            {/* Card Information */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <CreditCard size={20} className="text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Card Information</h3>
              </div>

              <div className="space-y-4">
                {/* Card Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={paymentDetails.cardNumber}
                      onChange={(e) => formatCardNumberInput(e.target.value.replace(/\D/g, ''))}
                      placeholder="1234 5678 9012 3456"
                      maxLength={cardBrand === 'amex' ? 17 : 19}
                      autoComplete="cc-number"
                      name="cardNumber"
                      id="cardNumber"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {cardBrand && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className={`text-xs font-medium px-2 py-1 rounded ${
                          cardBrand === 'visa' ? 'bg-blue-100 text-blue-800' :
                          cardBrand === 'mastercard' ? 'bg-red-100 text-red-800' :
                          cardBrand === 'amex' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {cardBrand.toUpperCase()}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.cardNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.cardNumber}</p>
                  )}
                </div>

                {/* Cardholder Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    value={paymentDetails.cardholderName}
                    onChange={(e) => updatePaymentDetail('cardholderName', e.target.value)}
                    placeholder="John Doe"
                    autoComplete="cc-name"
                    name="cardholderName"
                    id="cardholderName"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      errors.cardholderName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.cardholderName && (
                    <p className="text-red-500 text-sm mt-1">{errors.cardholderName}</p>
                  )}
                </div>

                {/* Expiry and CVV */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Month
                    </label>
                    <select
                      value={paymentDetails.expiryMonth}
                      onChange={(e) => updatePaymentDetail('expiryMonth', e.target.value)}
                      autoComplete="cc-exp-month"
                      name="expiryMonth"
                      id="expiryMonth"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.expiry ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">MM</option>
                      {months.map(month => (
                        <option key={month} value={month.toString().padStart(2, '0')}>
                          {month.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <select
                      value={paymentDetails.expiryYear}
                      onChange={(e) => updatePaymentDetail('expiryYear', e.target.value)}
                      autoComplete="cc-exp-year"
                      name="expiryYear"
                      id="expiryYear"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.expiry ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">YYYY</option>
                      {years.map(year => (
                        <option key={year} value={year.toString()}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CVV
                    </label>
                    <input
                      type="text"
                      value={paymentDetails.cvv}
                      onChange={(e) => updatePaymentDetail('cvv', e.target.value.replace(/\D/g, ''))}
                      placeholder={cardBrand === 'amex' ? '1234' : '123'}
                      maxLength={cardBrand === 'amex' ? 4 : 3}
                      autoComplete="cc-csc"
                      name="cvv"
                      id="cvv"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.cvv ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.cvv && (
                      <p className="text-red-500 text-sm mt-1">{errors.cvv}</p>
                    )}
                  </div>
                </div>

                {errors.expiry && (
                  <p className="text-red-500 text-sm">{errors.expiry}</p>
                )}
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={paymentDetails.billingAddress.street}
                    onChange={(e) => updatePaymentDetail('billingAddress.street', e.target.value)}
                    placeholder="123 Main Street"
                    autoComplete="billing street-address"
                    name="billingStreet"
                    id="billingStreet"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      errors['billingAddress.street'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors['billingAddress.street'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['billingAddress.street']}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={paymentDetails.billingAddress.city}
                      onChange={(e) => updatePaymentDetail('billingAddress.city', e.target.value)}
                      placeholder="San Francisco"
                      autoComplete="billing address-level2"
                      name="billingCity"
                      id="billingCity"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors['billingAddress.city'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors['billingAddress.city'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['billingAddress.city']}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={paymentDetails.billingAddress.state}
                      onChange={(e) => updatePaymentDetail('billingAddress.state', e.target.value)}
                      placeholder="CA"
                      autoComplete="billing address-level1"
                      name="billingState"
                      id="billingState"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors['billingAddress.state'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors['billingAddress.state'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['billingAddress.state']}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={paymentDetails.billingAddress.zipCode}
                      onChange={(e) => updatePaymentDetail('billingAddress.zipCode', e.target.value)}
                      placeholder="94102"
                      autoComplete="billing postal-code"
                      name="billingZip"
                      id="billingZip"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors['billingAddress.zipCode'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors['billingAddress.zipCode'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['billingAddress.zipCode']}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <select
                      value={paymentDetails.billingAddress.country}
                      onChange={(e) => updatePaymentDetail('billingAddress.country', e.target.value)}
                      autoComplete="billing country"
                      name="billingCountry"
                      id="billingCountry"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Card Options */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="saveCard"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="saveCard" className="text-sm font-medium text-gray-900">
                  Save this card for future donations
                </label>
              </div>

              {saveCard && (
                <div className="ml-7 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Nickname
                    </label>
                    <input
                      type="text"
                      value={cardNickname}
                      onChange={(e) => setCardNickname(e.target.value)}
                      placeholder="My main card"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.cardNickname ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.cardNickname && (
                      <p className="text-red-500 text-sm mt-1">{errors.cardNickname}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="makeDefault"
                      checked={makeDefault}
                      onChange={(e) => setMakeDefault(e.target.checked)}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="makeDefault" className="text-sm text-gray-700">
                      Make this my default payment method
                    </label>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* iDEAL Form */}
        {selectedPaymentType === 'ideal' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">iDEAL Payment Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Your Bank
                </label>
                <select
                  value={idealDetails.bank}
                  onChange={(e) => updateIdealDetail('bank', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.bank ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Choose your bank</option>
                  {IDEAL_BANKS.map(bank => (
                    <option key={bank.code} value={bank.code}>
                      {bank.name}
                    </option>
                  ))}
                </select>
                {errors.bank && (
                  <p className="text-red-500 text-sm mt-1">{errors.bank}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={idealDetails.accountHolder}
                  onChange={(e) => updateIdealDetail('accountHolder', e.target.value)}
                  placeholder="John Doe"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.accountHolder ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.accountHolder && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountHolder}</p>
                )}
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                You will be redirected to your bank to complete the payment securely.
              </p>
            </div>
          </div>
        )}

        {/* PayPal Form */}
        {selectedPaymentType === 'paypal' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">PayPal Payment</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PayPal Email
                </label>
                <input
                  type="email"
                  value={paypalDetails.email}
                  onChange={(e) => updatePaypalDetail('email', e.target.value)}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                You will be redirected to PayPal to complete your payment securely.
              </p>
            </div>
          </div>
        )}

        {/* Apple Pay / Google Pay */}
        {(selectedPaymentType === 'apple_pay' || selectedPaymentType === 'google_pay') && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedPaymentType === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}
            </h3>
            <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
              <Smartphone size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                Click the button below to pay with {selectedPaymentType === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}
              </p>
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield size={20} className="text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-green-800">Secure Payment</h4>
              <p className="text-green-700 text-sm">
                Your payment information is encrypted and secure.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isProcessing}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              <span>Processing Payment...</span>
            </>
          ) : (
            <>
              <Lock size={20} />
              <span>
                {selectedPaymentType === 'apple_pay' ? 'Pay with Apple Pay' :
                 selectedPaymentType === 'google_pay' ? 'Pay with Google Pay' :
                 selectedPaymentType === 'paypal' ? 'Continue to PayPal' :
                 selectedPaymentType === 'ideal' ? 'Continue to Bank' :
                 `Complete Dana $${amount.toFixed(2)}`}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}; 